import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminLiveSessionService, AdminSession, AdminSessionListResponse, AdminSessionFilterParams } from '@shared/services/live-sessions';
import { Subscription } from 'rxjs';
import { PaginationService, ToastService } from 'thkee-common';
import { CommonPaginationComponent } from '../../../@shared/components/common-pagination/common-pagination.component';
import { ProcessingDirective } from '../../../../../../thkee-common/src/lib/directives/processing.directive';
import { SessionFiltersComponent } from '../../../@shared/components/session-filters/session-filters.component';
import { SessionDrawerComponent } from '../session-drawer/session-drawer.component';
import { AdminMonitorComponent } from '../monitor/admin-monitor.component';

@Component({
  selector: 'app-admin-sessions-list',
  templateUrl: './admin-sessions-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    CommonPaginationComponent,
    ProcessingDirective,
    SessionFiltersComponent,
    SessionDrawerComponent,
    AdminMonitorComponent,
  ],
})
export class AdminSessionsListComponent implements OnInit, OnDestroy {
  private readonly adminService = inject(AdminLiveSessionService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly paginationService = inject(PaginationService);
  private readonly toastService = inject(ToastService);

  sessions = signal<AdminSession[]>([]);
  totalSessions = signal(0);
  isLoading = signal(false);
  activeTab = signal<'sessions' | 'monitor'>('sessions');

  queryParams: AdminSessionFilterParams = { page: 1, page_size: 10 };

  paginationConfig = {
    pageCountSummary: true,
    pageSize: true,
    firstAndLast: true,
  };

  @ViewChild('sessionDrawer') private sessionDrawer!: SessionDrawerComponent;

  private subscriptions = new Subscription();

  private initialLoadDone = false;

  ngOnInit(): void {
    // Read tab from URL
    const urlParams = this.route.snapshot.queryParams;
    if (urlParams['tab'] === 'monitor') {
      this.activeTab.set('monitor');
    }
    // Don't call loadSessions() here — wait for filtersChanged from the
    // filter component's initialValue subscription, which includes URL-restored filters.
  }

  loadSessions(): void {
    this.isLoading.set(true);
    const params: Record<string, string | number | boolean> = {};
    Object.entries(this.queryParams).forEach(([key, value]) => {
      if (value !== '' && value !== undefined && value !== null) {
        params[key] = value;
      }
    });

    this.subscriptions.add(
      this.adminService.getAdminSessions(params).subscribe({
        next: (res: AdminSessionListResponse) => {
          this.sessions.set(res.results);
          this.totalSessions.set(res.count);
          this.paginationService.getPager(res.count, this.queryParams.page!, this.queryParams.page_size!);
          this.isLoading.set(false);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.toastService.error({ message: err?.error?.detail || 'Failed to load sessions' });
        },
      })
    );
  }

  // Row click -> open drawer
  openDrawer(session: AdminSession): void {
    this.sessionDrawer.openSession(session);
  }

  // Session ID link -> navigate to detail
  viewSession(event: Event, session: AdminSession): void {
    event.stopPropagation();
    this.router.navigate(['/live-sessions/sessions', session.id]);
  }

  // Filters changed from filter component (also fires on initial URL restore)
  onFiltersChanged(filters: AdminSessionFilterParams): void {
    if (!this.initialLoadDone) {
      // First call is from initialValue — use restored page/page_size from URL if present
      const urlParams = this.route.snapshot.queryParams;
      this.queryParams = {
        ...filters,
        page: filters.page || Number(urlParams['page']) || 1,
        page_size: filters.page_size || Number(urlParams['page_size']) || 10,
      };
      this.initialLoadDone = true;
    } else {
      this.queryParams = { ...filters, page: 1, page_size: this.queryParams.page_size };
    }
    this.loadSessions();
  }

  // Tab switching
  switchTab(tab: 'sessions' | 'monitor'): void {
    this.activeTab.set(tab);
    this.router.navigate([], {
      queryParams: { tab },
      queryParamsHandling: 'merge',
    });
  }

  // Pagination
  paginationAction(page: number): void {
    this.queryParams.page = page;
    this.loadSessions();
  }

  pageSizeAction(value: number): void {
    this.queryParams.page_size = value;
    this.queryParams.page = 1;
    this.loadSessions();
  }

  // Status badge styling
  getTemporalStatusClass(status: string): string {
    switch (status) {
      case 'upcoming': return 'tag-blue';
      case 'in_progress': return 'tag-green';
      case 'completed': return 'tag-neutral';
      case 'cancelled': return 'tag-red';
      default: return 'tag-neutral';
    }
  }

  getTemporalStatusLabel(status: string): string {
    switch (status) {
      case 'upcoming': return 'Upcoming';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  }

  formatDuration(minutes: number | undefined): string {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
